import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import DeliveryScheduleTableItem from 'components/delivery-schedule/delivery-schedule-table-item.component';
import { productionOverviewOrdersTemplate } from 'apollo/templates/production-overview';
import { Order } from 'models/order';
import { ProductionOverviewCellInput, ProductionOverviewCountMode } from 'models/production-overview';
import { printProductionOverviewOrders } from './print-production-overview-orders';

export type OverviewOrdersDialogState = {
    /** The heading parts of what was clicked, e.g. ["Received", "Via scanner case", "2026-10-30"]. */
    labelParts: string[];
    /** One cell per disjoint count behind the clicked number; the lists are concatenated. */
    cells: ProductionOverviewCellInput[];
};

type OverviewOrdersDialogProps = {
    dialogState: OverviewOrdersDialogState;
    countMode: ProductionOverviewCountMode;
    onClose: () => void;
};

const OverviewOrdersDialog: FC<OverviewOrdersDialogProps> = ({
    dialogState,
    countMode,
    onClose,
}: OverviewOrdersDialogProps) => {
    const { t: translate } = useTranslation(['production-overview', 'common', 'orders', 'delivery-schedule']);
    const apolloClient = useApolloClient();
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [loadError, setLoadError] = useState<Error | null>(null);

    // A clicked number may stand for several disjoint cells (a group total, a department
    // row); the click-through lists are always fetched live so they match the backend now.
    useEffect(() => {
        let isStale = false;
        setOrders(null);
        setLoadError(null);
        Promise.all(
            dialogState.cells.map((cell) =>
                apolloClient.query({
                    query: productionOverviewOrdersTemplate,
                    variables: { cell },
                    fetchPolicy: 'network-only',
                }),
            ),
        )
            .then((results) => {
                if (isStale) return;
                const ordersById = new Map<string, Order>();
                results
                    .flatMap((result) => (result.data?.productionOverviewOrders || []) as Order[])
                    .forEach((order) => ordersById.set(order._id, order));
                setOrders(Array.from(ordersById.values()));
            })
            .catch((error) => {
                if (!isStale) setLoadError(error);
            });
        return () => {
            isStale = true;
        };
    }, [dialogState]);

    const cameInViaLabelFor = (order: Order): string =>
        order.cameInVia ? translate(`production-overview:CAME_IN_VIA_${order.cameInVia}`) : '-';

    const dialogTitle = dialogState.labelParts.join(' · ');
    const totalActionCount = (orders || []).reduce((total, order) => total + (order.actionsCount || 0), 0);

    const countHeading =
        countMode === ProductionOverviewCountMode.ACTIONS
            ? translate('production-overview:DIALOG_ACTIONS_ACROSS_ORDERS', {
                  actionCount: totalActionCount,
                  orderCount: orders?.length || 0,
              })
            : translate('production-overview:DIALOG_ORDER_COUNT', { count: orders?.length || 0 });

    return (
        <DHDialog open onClose={onClose} maxWidth="lg" fullWidth title={dialogTitle}>
            {!orders && !loadError ? <Loader /> : undefined}
            {loadError ? (
                <Typography variant="body2" color="error">
                    {translate('production-overview:DIALOG_LOAD_ERROR')}
                </Typography>
            ) : undefined}
            {orders ? (
                <Box>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Typography variant="body2" data-test="production-overview.dialog-count">
                            {countHeading}
                        </Typography>
                        <Box flexGrow={1} />
                        <Button
                            size="small"
                            startIcon={<PrintIcon />}
                            disabled={!orders.length}
                            onClick={() =>
                                printProductionOverviewOrders(orders, cameInViaLabelFor, dialogTitle, translate)
                            }
                        >
                            {translate('orders:PRINT')}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => window.open('/orders/list', '_blank')}
                        >
                            {translate('production-overview:OPEN_FULL_LIST_IN_ORDERS')}
                        </Button>
                    </Box>
                    {orders.length ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                    <TableCell>{translate('common:PATIENT')}</TableCell>
                                    <TableCell>{translate('delivery-schedule:DELIVERY_TO')}</TableCell>
                                    <TableCell>{translate('common:DENTIST')}</TableCell>
                                    <TableCell>{translate('orders:DELIVERY_DATE')}</TableCell>
                                    <TableCell>{translate('orders:WORKFLOW_STEP')}</TableCell>
                                    <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                    <TableCell>{translate('production-overview:CAME_IN_VIA')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orders.map((order) => (
                                    <DeliveryScheduleTableItem
                                        key={order._id}
                                        order={order}
                                        isExternal={false}
                                        readOnly
                                        cameInViaLabel={cameInViaLabelFor(order)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {translate('orders:NO_ORDER_FOUND')}
                        </Typography>
                    )}
                </Box>
            ) : undefined}
        </DHDialog>
    );
};

export default OverviewOrdersDialog;
